import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle } from 'lucide-react';
import { sendNotification } from '@/services/mushinNotifier';
import { buildDemoEmailHtml, getNotificationRecipient } from '@/services/formEmailHtml';

const RED = '#E02D45';

export default function BookDemoDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', contact: '', company_name: '', plant_location: '', requirement: ''
  });

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const to = getNotificationRecipient();
      const subject = `New Demo Request from ${form.name} - ${form.company_name}`;
      await sendNotification({
        channel: 'email',
        to,
        subject,
        html: buildDemoEmailHtml(form),
      });
      setSuccess(true);
      setForm({ name: '', email: '', contact: '', company_name: '', plant_location: '', requirement: '' });
    } catch (err) {
      console.error('Demo submission failed:', err);
    }
    setLoading(false);
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) setTimeout(() => setSuccess(false), 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/[0.08] text-white sm:max-w-lg" data-testid="demo-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black uppercase tracking-tight text-white">Book a Demo</DialogTitle>
          <DialogDescription className="text-white/35 text-[13px] font-medium">
            Tell us about your requirements and our team will schedule a personalized walkthrough.
          </DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="py-10 text-center" data-testid="demo-success">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
              <CheckCircle className="w-7 h-7" style={{ color: RED }} />
            </div>
            <p className="text-lg font-black uppercase text-white">Thank You</p>
            <p className="text-sm text-white/35 mt-2 font-medium">Our team will reach out to you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="demo-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Name *</Label>
                <Input required value={form.name} onChange={update('name')} placeholder="Your full name"
                  className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="demo-name" />
              </div>
              <div>
                <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Email *</Label>
                <Input required type="email" value={form.email} onChange={update('email')} placeholder="work@company.com"
                  className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="demo-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Contact Number *</Label>
                <Input required value={form.contact} onChange={update('contact')} placeholder="+91 98xxx xxxxx"
                  className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="demo-contact" />
              </div>
              <div>
                <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Company Name *</Label>
                <Input required value={form.company_name} onChange={update('company_name')} placeholder="Company name"
                  className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="demo-company" />
              </div>
            </div>
            <div>
              <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Plant Location / Address</Label>
              <Input value={form.plant_location} onChange={update('plant_location')} placeholder="City, State"
                className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20" data-testid="demo-location" />
            </div>
            <div>
              <Label className="text-white/30 text-[10px] font-bold uppercase tracking-[0.15em]">Requirement Description</Label>
              <Textarea value={form.requirement} onChange={update('requirement')} placeholder="What are you looking to digitize or improve?"
                className="bg-white/[0.04] border-white/[0.08] text-white mt-1.5 focus-visible:ring-[#E02D45] placeholder:text-white/20 min-h-[80px]" data-testid="demo-requirement" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full text-white py-3 rounded-full font-bold text-sm transition-all duration-300 disabled:opacity-50 hover:shadow-lg"
              style={{ backgroundColor: RED }}
              data-testid="demo-submit">
              {loading ? 'Submitting...' : 'Request Demo'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
